import React from 'react';
import { User } from 'lucide-react';

/**
 * UserAvatar Component
 * Displays user avatar with fallback to initials or icon
 */
export const UserAvatar = ({ 
  user, 
  size = 'md', 
  showName = false,
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
  };

  const getInitials = (name) => {
    if (!name) return '?';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return 'bg-gray-500';
    
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-cyan-500',
    ];
    
    // Generate consistent color based on name
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const userName = user?.name || user?.email || 'Unknown';
  const avatarUrl = user?.avatar_url || user?.avatarUrl;
  const initials = getInitials(userName);
  const bgColor = getAvatarColor(userName);

  const avatarContent = avatarUrl ? (
    <img
      src={avatarUrl}
      alt={userName}
      className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
      onError={(e) => {
        // Fallback to initials if image fails to load
        e.target.style.display = 'none';
      }}
    />
  ) : (
    <div
      className={`${sizeClasses[size]} rounded-full ${bgColor} flex items-center justify-center text-white font-medium ${className}`}
    >
      {initials === '?' ? (
        <User className={iconSizes[size]} />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );

  if (showName) {
    return (
      <div className="flex items-center gap-2">
        {avatarContent}
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {userName}
          </span>
          {user?.email && user.email !== userName && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {user.email}
            </span>
          )}
        </div>
      </div>
    );
  }

  return avatarContent;
};

export default UserAvatar;
